(() => {
  'use strict';

  const FAVORITES_KEY = 'bigfan:favorites:v1';
  const RECENT_KEY = 'bigfan:recent:v1';
  const COLLECTION_STATE_PREFIX = 'bigfan:collection:';
  const RECENT_PRODUCT_CACHE = new Map();

  const parseJSON = (value, fallback) => {
    if (value == null || value === '') return fallback;
    try {
      const parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  };

  const readStorage = (key, fallback) => {
    try { return parseJSON(localStorage.getItem(key), fallback); } catch (_) { return fallback; }
  };

  const readArrayStorage = (key) => {
    const value = readStorage(key, []);
    return Array.isArray(value) ? value : [];
  };
  const writeStorage = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  // Browser-local favorites only. Customer account cross-device synchronization
  // requires a separately authenticated Shopify app + persistent account storage.
  let favoritesClickBound = false;
  let favoritesStorageBound = false;
  let favoriteNoticeTimeout = 0;

  const showFavoritesError = (message = 'No se ha podido guardar el favorito en este navegador. Comprueba que permites el almacenamiento local.') => {
    let notice = document.getElementById('BigFanFavoritesStorageError');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'BigFanFavoritesStorageError';
      notice.className = 'bigfan-favorites-notice';
      notice.setAttribute('role', 'alert');
      document.body.appendChild(notice);
    }
    notice.textContent = message;
    notice.hidden = false;
    window.clearTimeout(favoriteNoticeTimeout);
    favoriteNoticeTimeout = window.setTimeout(() => { notice.hidden = true; }, 6500);
  };

  // Never turn an unreadable/corrupt favorites list into an empty list: doing
  // so could overwrite the customer's previously saved favorites on click.
  const readFavorites = () => {
    let raw;
    try {
      raw = window.localStorage.getItem(FAVORITES_KEY);
    } catch (error) {
      console.error('[BigFan] Could not read browser favorites.', error);
      showFavoritesError('No se pueden leer tus favoritos en este navegador. Comprueba los permisos de almacenamiento.');
      return null;
    }
    if (raw === null) return new Set(); // Fresh visitor: genuinely no favorites.
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.some((handle) => typeof handle !== 'string' || handle.length === 0)) {
        throw new Error('Invalid favorites data format');
      }
      return new Set(parsed);
    } catch (error) {
      console.error('[BigFan] Invalid persisted favorites; original data preserved.', error);
      showFavoritesError('No se pueden leer tus favoritos guardados. No se modificará la lista existente.');
      return null;
    }
  };

  const updateFavoritesHeader = (favorites) => {
    document.querySelectorAll('[data-bigfan-favorites-link]').forEach((link) => {
      const count = link.querySelector('[data-bigfan-favorites-count]');
      if (!count) return;
      if (favorites === null) {
        // An unreadable favorites list must not appear as an empty one.
        count.hidden = true;
        link.classList.remove('has-favorites');
        link.setAttribute('aria-label', 'Mis favoritos');
        return;
      }
      const total = favorites.size;
      link.classList.toggle('has-favorites', total > 0);
      count.hidden = total === 0;
      count.textContent = total > 99 ? '99+' : String(total);
      link.setAttribute('aria-label', total === 0 ? 'Mis favoritos: ninguno' : `Mis favoritos: ${total} productos`);
    });
  };

  const notifyFavoritesChanged = (favorites) => {
    document.dispatchEvent(new CustomEvent('bigfan:favorites-changed', {
      detail: { handles: [...favorites] }
    }));
  };

  const syncFavoriteButtons = (favorites, root = document) => {
    updateFavoritesHeader(favorites);
    if (favorites === null) return;
    const buttons = Array.from(root.querySelectorAll('[data-bigfan-favorite]'));
    if (root.matches?.('[data-bigfan-favorite]')) buttons.unshift(root);
    buttons.forEach((button) => {
      const active = favorites.has(button.dataset.productHandle);
      const title = button.dataset.productTitle || 'este producto';
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? `Quitar ${title} de favoritos` : `Añadir ${title} a favoritos`);
      button.title = active ? 'Quitar de favoritos' : 'Añadir a favoritos';
      button.dataset.bigfanBound = 'true';
    });
  };

  const initFavorites = (root = document) => {
    syncFavoriteButtons(readFavorites(), root);
    if (!favoritesClickBound) {
      favoritesClickBound = true;
      // Capture before stretched product-card links/gallery handlers. Delegation
      // works on product pages and on dynamically appended infinite-scroll cards.
      document.addEventListener('click', (event) => {
        const button = event.target instanceof Element
          ? event.target.closest('[data-bigfan-favorite]')
          : null;
        if (!button || button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const handle = button.dataset.productHandle;
        if (!handle) {
          console.error('[BigFan] Favorite button lacks product handle.');
          showFavoritesError('No se puede marcar este producto como favorito. Actualiza la página e inténtalo de nuevo.');
          return;
        }
        const favorites = readFavorites();
        if (favorites === null) return;
        if (favorites.has(handle)) favorites.delete(handle);
        else favorites.add(handle);
        try {
          const serialized = JSON.stringify([...favorites]);
          window.localStorage.setItem(FAVORITES_KEY, serialized);
          if (window.localStorage.getItem(FAVORITES_KEY) !== serialized) {
            throw new Error('Favorites could not be verified after writing');
          }
        } catch (error) {
          console.error('[BigFan] Could not persist favorites.', error);
          showFavoritesError();
          return;
        }
        syncFavoriteButtons(favorites);
        notifyFavoritesChanged(favorites);
      }, true);
    }
    if (!favoritesStorageBound) {
      favoritesStorageBound = true;
      window.addEventListener('storage', (event) => {
        if (event.key === FAVORITES_KEY || event.key === null) {
          const latest = readFavorites();
          syncFavoriteButtons(latest);
          if (latest !== null) notifyFavoritesChanged(latest);
        }
      });
    }
  };

  const favoriteProductCache = new Map();
  const fetchFavoriteProduct = async (handle, productsBase) => {
    if (favoriteProductCache.has(handle)) return favoriteProductCache.get(handle);
    const request = (async () => {
      const url = new URL(`${productsBase}${encodeURIComponent(handle)}.js`, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid product URL');
      const response = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      if (!product || product.handle !== handle || !Array.isArray(product.variants)) {
        throw new Error('Invalid product payload');
      }
      return product;
    })().catch((error) => {
      favoriteProductCache.delete(handle); // an explicit retry must refetch errors
      throw error;
    });
    favoriteProductCache.set(handle, request);
    return request;
  };

  const favoriteHeartMarkup = (handle, title) => `
    <button type="button" class="bigfan-favorite-button" data-bigfan-favorite
      data-product-handle="${escapeHTML(handle)}" data-product-title="${escapeHTML(title)}"
      aria-label="Quitar ${escapeHTML(title)} de favoritos" aria-pressed="true">
      <span class="svg-wrapper bigfan-favorite-icon" aria-hidden="true">
        <span class="bigfan-favorite-icon__outline"><svg viewBox="0 0 20 20" fill="currentColor" focusable="false"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg></span>
        <svg class="bigfan-favorite-icon__filled" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
      </span>
    </button>`;

  const initFavoritesPage = (root = document) => {
    const page = root.querySelector?.('[data-bigfan-favorites-page]');
    if (!page || page.dataset.bigfanFavoritesPageBound === 'true') return;
    page.dataset.bigfanFavoritesPageBound = 'true';
    const grid = page.querySelector('[data-bigfan-favorites-grid]');
    const status = page.querySelector('[data-bigfan-favorites-status]');
    const warning = page.querySelector('[data-bigfan-favorites-warning]');
    const browse = page.querySelector('[data-bigfan-favorites-browse]');
    const productsBase = page.dataset.productsBase;
    const locale = page.dataset.locale || 'es-ES';
    const currency = page.dataset.currency || 'EUR';
    const money = (cents) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    let renderVersion = 0;
    const render = async () => {
      const version = ++renderVersion;
      const saved = readFavorites();
      grid.replaceChildren();
      warning.hidden = true;
      browse.hidden = true;
      if (saved === null) { status.textContent = 'No se pueden leer los favoritos guardados en este navegador.'; return; }
      const handles = [...saved];
      if (!handles.length) {
        status.textContent = 'Todavía no has guardado ningún producto como favorito.';
        browse.hidden = false;
        return;
      }
      status.textContent = `Cargando ${handles.length} producto${handles.length === 1 ? '' : 's'}…`;
      // Limited concurrency: do not launch dozens of product requests at once.
      const results = [];
      for (let index = 0; index < handles.length; index += 6) {
        const batch = await Promise.allSettled(handles.slice(index, index + 6).map((handle) => fetchFavoriteProduct(handle, productsBase)));
        if (version !== renderVersion) return;
        results.push(...batch);
      }
      if (version !== renderVersion) return;
      let errors = 0;
      results.forEach((result, index) => {
        const handle = handles[index];
        if (result.status === 'rejected') {
          errors++;
          console.warn('[BigFan] Favorite could not be loaded:', handle, result.reason);
          const failed = document.createElement('div');
          failed.className = 'bigfan-favorites-page__failure';
          const text = document.createElement('p');
          text.textContent = `No se ha podido cargar el favorito «${handle}». No se ha eliminado de tu lista.`;
          const retry = document.createElement('button');
          retry.type = 'button'; retry.textContent = 'Reintentar';
          retry.addEventListener('click', render);
          failed.append(text, retry); grid.append(failed);
          return;
        }
        const product = result.value;
        const productUrl = new URL(`${productsBase}${encodeURIComponent(handle)}`, location.origin).pathname;
        const title = typeof product.title === 'string' ? product.title : handle;
        const available = product.available === true && product.variants.some((variant) => variant.available === true);
        const variants = product.variants.filter((variant) => variant.available === true);
        const singleVariant = product.variants.length === 1 && variants.length === 1;
        const rawImage = product.featured_image;
        let imageUrl = '';
        if (typeof rawImage === 'string' && rawImage) {
          try {
            const url = new URL(rawImage, location.origin);
            if (url.protocol === 'https:' || url.protocol === 'http:') imageUrl = url.href;
          } catch (error) { console.warn('[BigFan] Invalid favorite image', handle, error); }
        }
        const card = document.createElement('article');
        card.className = 'bigfan-wishlist-card';
        const media = document.createElement('div'); media.className = 'bigfan-wishlist-card__media';
        const link = document.createElement('a'); link.href = productUrl; link.className = 'bigfan-wishlist-card__link';
        if (imageUrl) {
          const image = document.createElement('img'); image.src = imageUrl; image.alt = title;
          image.loading = 'lazy'; image.width = 480; image.height = 480;
          link.append(image);
        } else {
          const noImage = document.createElement('span'); noImage.textContent = 'Imagen no disponible'; link.append(noImage);
        }
        media.append(link);
        const favoriteContainer = document.createElement('div');
        favoriteContainer.innerHTML = favoriteHeartMarkup(handle, title);
        media.append(favoriteContainer.firstElementChild);
        const details = document.createElement('div'); details.className = 'bigfan-wishlist-card__details';
        const heading = document.createElement('h2'); heading.className = 'bigfan-wishlist-card__title';
        const headingLink = document.createElement('a'); headingLink.href = productUrl; headingLink.textContent = title;
        heading.append(headingLink); details.append(heading);
        if (product.vendor) { const vendor = document.createElement('p'); vendor.className = 'bigfan-wishlist-card__vendor'; vendor.textContent = product.vendor; details.append(vendor); }
        const footer = document.createElement('div'); footer.className = 'bigfan-wishlist-card__footer';
        const price = document.createElement('span'); price.className = 'bigfan-wishlist-card__price';
        price.textContent = Number.isFinite(Number(product.price)) ? money(Number(product.price)) : 'Precio no disponible';
        footer.append(price);
        if (document.documentElement.classList.contains('bigfan-catalog-mode')) {
          // Catalog: favorites remain usable, but never generate a purchase form.
        } else if (singleVariant) {
          const form = document.createElement('product-form'); form.dataset.hideErrors = 'false';
          form.innerHTML = `<div class="product-form__error-message-wrapper" role="alert" hidden><span class="product-form__error-message"></span></div>
            <form method="post" action="${escapeHTML(window.routes?.cart_add_url || '/cart/add')}" data-type="add-to-cart-form">
              <input class="product-variant-id" type="hidden" name="id" value="${variants[0].id}">
              <button type="submit" class="bigfan-wishlist-card__cart" aria-label="Añadir ${escapeHTML(title)} al carrito">
                <span class="bigfan-wishlist-card__cart-icon" aria-hidden="true">${document.querySelector('#cart-icon-bubble .svg-wrapper')?.innerHTML || '<svg viewBox="0 0 24 24"><path d="M3 4h2l3 12h11l2-9H7M9 21h.01M18 21h.01" fill="none" stroke="currentColor" stroke-width="2"/></svg>'}</span>
                <span class="loading__spinner hidden" aria-hidden="true"></span><span class="sold-out-message hidden">Agotado</span>
              </button>
            </form>`;
          footer.append(form);
        } else if (available) {
          const options = document.createElement('a'); options.href = productUrl;
          options.className = 'bigfan-wishlist-card__cart'; options.title = 'Elegir opciones';
          options.setAttribute('aria-label', `Ver opciones de ${title}`);
          options.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12h16m-7-7 7 7-7 7"/></svg>';
          footer.append(options);
        } else {
          const sold = document.createElement('span'); sold.className = 'bigfan-wishlist-card__sold'; sold.textContent = 'Agotado'; footer.append(sold);
        }
        details.append(footer); card.append(media, details); grid.append(card);
      });
      status.textContent = `${handles.length} producto${handles.length === 1 ? '' : 's'} guardado${handles.length === 1 ? '' : 's'}.`;
      if (errors) { warning.textContent = `${errors} favorito${errors === 1 ? '' : 's'} no se ha${errors === 1 ? '' : 'n'} podido cargar. Puedes reintentar sin perderlos.`; warning.hidden = false; }
      initFavorites(grid);
    };
    document.addEventListener('bigfan:favorites-changed', render);
    render();
  };

  const storeCurrentProduct = () => {
    const node = document.querySelector('[data-bigfan-current-product]');
    if (!node) return;
    const product = parseJSON(node.textContent, null);
    if (!product?.handle) return;
    const current = readArrayStorage(RECENT_KEY).filter((item) => item?.handle && item.handle !== product.handle);
    current.unshift(product);
    writeStorage(RECENT_KEY, current.slice(0, 12));
  };

  const escapeHTML = (value = '') => String(value)
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');

  const fetchRecentProductData = async (item) => {
    if (!item?.handle || !item?.url) return null;
    if (RECENT_PRODUCT_CACHE.has(item.handle)) return RECENT_PRODUCT_CACHE.get(item.handle);

    const request = (async () => {
      try {
        const url = new URL(item.url, window.location.origin);
        url.search = '';
        url.hash = '';
        const path = url.pathname.replace(/\/$/, '');
        const response = await fetch(`${path}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const product = await response.json();
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const availableVariant = variants.find((variant) => variant?.available === true) || null;
        const available = typeof product.available === 'boolean'
          ? product.available
          : Boolean(availableVariant);
        const rawImages = [product.featured_image, ...(Array.isArray(product.images) ? product.images : [])];
        const images = [];
        rawImages.forEach((candidate) => {
          const src = typeof candidate === 'string' ? candidate : candidate?.src;
          if (!src || images.includes(src)) return;
          images.push(src);
        });
        return {
          available: Boolean(available),
          title: typeof product.title === 'string' && product.title ? product.title : item.title,
          image: images[0] || item.image,
          images: images.slice(0, 8),
          variantId: availableVariant?.id ? String(availableVariant.id) : '',
          variantCount: variants.length,
          priceCents: Number.isFinite(Number(product.price)) ? Number(product.price) : null,
          compareAtPriceCents: Number.isFinite(Number(product.compare_at_price)) ? Number(product.compare_at_price) : null,
        };
      } catch (error) {
        console.warn('[BigFan] Could not refresh recently viewed product; item hidden.', item.handle, error);
        return null;
      }
    })();
    RECENT_PRODUCT_CACHE.set(item.handle, request);
    return request;
  };

  const formatRecentMoney = (cents, currency, locale) => {
    if (!Number.isFinite(cents) || !currency) return null;
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_) {
      return null;
    }
  };

  const recentCartIcon = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="9" cy="20" r="1.55" fill="currentColor"></circle><circle cx="18" cy="20" r="1.55" fill="currentColor"></circle><path d="M3 4h2.2l2.15 10.1a1.1 1.1 0 0 0 1.08.9h8.9a1.1 1.1 0 0 0 1.06-.82L21 8H7.25" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  const recentSpinner = '<span class="loading__spinner hidden" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" focusable="false"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';

  const renderRecentGallery = (item) => {
    const images = Array.isArray(item.images) && item.images.length ? item.images : (item.image ? [item.image] : []);
    if (!images.length) return '';
    const title = escapeHTML(item.title);
    const url = escapeHTML(item.url);
    if (images.length === 1) {
      return `<a class="bigfan-recent-card__media-link" href="${url}" aria-label="Ver ${title}"><img src="${escapeHTML(images[0])}" alt="${title}" loading="lazy" width="600" height="600"></a>`;
    }
    const visibleDots = Math.min(images.length, 5);
    const templateImages = images.slice(1).map((src, index) => `<img data-bigfan-card-gallery-image data-bigfan-gallery-lazy data-gallery-index="${index + 1}" data-src="${escapeHTML(src)}" alt="${title}" class="bigfan-card-gallery__image" loading="lazy" width="600" height="600">`).join('');
    const dots = Array.from({ length: visibleDots }, (_, index) => `<span class="bigfan-card-gallery__dot${index === 0 ? ' is-active' : ''}" data-bigfan-card-gallery-dot data-gallery-dot-index="${index}"></span>`).join('');
    return `
      <div class="bigfan-card-gallery" data-bigfan-card-gallery data-gallery-count="${images.length}" data-product-id="recent-${escapeHTML(item.handle)}">
        <a class="bigfan-card-gallery__viewport" href="${url}" data-bigfan-card-gallery-viewport aria-label="Ver ${title}">
          <span class="bigfan-card-gallery__track" data-bigfan-card-gallery-track>
            <img src="${escapeHTML(images[0])}" alt="${title}" class="bigfan-card-gallery__image is-active" data-bigfan-card-gallery-image data-gallery-index="0" loading="lazy" width="600" height="600">
          </span>
        </a>
        <template data-bigfan-card-gallery-template>${templateImages}</template>
        <span class="bigfan-card-gallery__controls">
          <button type="button" class="bigfan-card-gallery__arrow bigfan-card-gallery__arrow--prev" data-bigfan-card-gallery-prev aria-label="Imagen anterior de ${title}"><span aria-hidden="true">‹</span></button>
          <button type="button" class="bigfan-card-gallery__arrow bigfan-card-gallery__arrow--next" data-bigfan-card-gallery-next aria-label="Imagen siguiente de ${title}"><span aria-hidden="true">›</span></button>
          <span class="bigfan-card-gallery__counter" data-bigfan-card-gallery-counter aria-live="polite" aria-atomic="true">
            <span class="visually-hidden" data-bigfan-card-gallery-current>Imagen 1 de ${images.length}</span>
            <span class="bigfan-card-gallery__dots" aria-hidden="true">${dots}</span>
          </span>
        </span>
      </div>`;
  };

  const renderRecentQuickAdd = (item) => {
    if (document.documentElement.classList.contains('bigfan-catalog-mode')) return '';
    if (!item.available || !item.variantId) return '';
    const title = escapeHTML(item.title);
    const safeHandle = String(item.handle || 'product').replace(/[^a-zA-Z0-9_-]/g, '-');
    const buttonContent = `<span class="visually-hidden bigfan-recent-cart-label">Agregar ${title} al carrito</span><span class="bigfan-recent-cart-icon" aria-hidden="true">${recentCartIcon}</span>${recentSpinner}`;
    if (Number(item.variantCount || 0) > 1) {
      const modalId = `QuickAdd-Recent-${safeHandle}`;
      return `
        <div class="bigfan-recent-card__quick-add">
          <modal-opener data-modal="#${modalId}">
            <button type="button" class="bigfan-recent-cart-button bigfan-recent-options-button" aria-haspopup="dialog" data-product-url="${escapeHTML(item.url)}" aria-label="Elegir opciones para ${title}">${buttonContent}</button>
          </modal-opener>
        </div>
        <quick-add-modal id="${modalId}" class="quick-add-modal">
          <div role="dialog" aria-label="Elegir opciones para ${title}" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">
            <button id="ModalClose-Recent-${safeHandle}" type="button" class="quick-add-modal__toggle" aria-label="Cerrar"><span aria-hidden="true">×</span></button>
            <div id="QuickAddInfo-Recent-${safeHandle}" class="quick-add-modal__content-info"></div>
          </div>
        </quick-add-modal>`;
    }
    const formId = `recent-add-${safeHandle}-${item.variantId}`;
    return `
      <div class="bigfan-recent-card__quick-add">
        <product-form data-hide-errors="false">
          <form method="post" action="${escapeHTML(window.routes?.cart_add_url || '/cart/add')}" id="${formId}" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="id" value="${escapeHTML(item.variantId)}" class="product-variant-id">
            <button type="submit" name="add" class="bigfan-recent-cart-button" aria-label="Agregar ${title} al carrito" aria-live="polite" data-sold-out-message="true">${buttonContent}<span class="sold-out-message hidden">Agotado</span></button>
            <div class="product-form__error-message-wrapper" role="alert" hidden><span class="product-form__error-message"></span></div>
          </form>
        </product-form>
      </div>`;
  };

  const renderRecentlyViewed = async (root = document) => {
    const sections = [...root.querySelectorAll('[data-bigfan-recently-viewed]')];
    await Promise.all(sections.map(async (section) => {
      const grid = section.querySelector('[data-bigfan-recent-grid]');
      if (!grid) return;
      const currentHandle = section.dataset.currentHandle || '';
      const limit = Math.max(2, Number(section.dataset.limit || 4));
      const hideOutOfStock = section.dataset.hideOutOfStock === 'true';
      const currency = section.dataset.currency || '';
      const locale = section.dataset.locale || document.documentElement.lang || '';
      let items = readArrayStorage(RECENT_KEY).filter((item) => item?.handle && item.handle !== currentHandle);

      if (items.length) {
        items = items.slice(0, Math.min(12, hideOutOfStock ? limit * 2 : limit));
        const checked = await Promise.all(items.map(async (item) => {
          const current = await fetchRecentProductData(item);
          if (!current) return null;
          if (hideOutOfStock && !current.available) return null;
          const currentPrice = formatRecentMoney(current.priceCents, currency, locale);
          const currentCompare = current.compareAtPriceCents > current.priceCents
            ? formatRecentMoney(current.compareAtPriceCents, currency, locale)
            : null;
          return {
            ...item,
            title: current.title || item.title,
            image: current.image || item.image,
            images: current.images || [],
            available: current.available,
            variantId: current.variantId,
            variantCount: current.variantCount,
            price: currentPrice || item.price,
            compareAtPrice: currentCompare,
          };
        }));
        items = checked.filter(Boolean);
      }
      items = items.slice(0, limit);

      if (!items.length) {
        section.hidden = true;
        grid.innerHTML = '';
        return;
      }
      section.hidden = false;
      grid.innerHTML = items.map((item) => `
        <div class="bigfan-recent-card-wrap">
          <article class="bigfan-recent-card">
            <div class="bigfan-recent-card__media">${renderRecentGallery(item)}</div>
            <div class="bigfan-recent-card__body">
              <a class="bigfan-recent-card__title" href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.title)}</strong></a>
              <div class="bigfan-recent-card__footer">
                <div class="bigfan-recent-card__price"><span>${escapeHTML(item.price)}</span>${item.compareAtPrice ? `<span class="bigfan-recent-card__compare">${escapeHTML(item.compareAtPrice)}</span>` : ''}</div>
                ${renderRecentQuickAdd(item)}
              </div>
            </div>
          </article>
          ${section.dataset.favoritesEnabled === 'true' ? `<button type="button" class="bigfan-favorite-button" data-bigfan-favorite data-product-handle="${escapeHTML(item.handle)}" data-product-title="${escapeHTML(item.title)}" aria-label="Añadir ${escapeHTML(item.title)} a favoritos" aria-pressed="false"><span class="svg-wrapper bigfan-favorite-icon" aria-hidden="true"><span class="bigfan-favorite-icon__outline"><svg viewBox="0 0 20 20" fill="currentColor" focusable="false"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg></span><svg class="bigfan-favorite-icon__filled" viewBox="0 0 24 24" fill="currentColor" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg></span></button>` : ''}
        </div>`).join('');
      initFavorites(grid);
      initProductCardGalleries(grid);
    }));
  };

  const initStickyATC = () => {
    const bar = document.querySelector('[data-bigfan-sticky-atc]');
    const main = document.querySelector('product-info');
    if (!bar || !main || bar.dataset.bigfanBound === 'true') return;
    bar.dataset.bigfanBound = 'true';
    const primaryButton = main.querySelector('product-form button[name="add"]');
    if (!primaryButton) return;
    const stickyButton = bar.querySelector('[data-bigfan-sticky-submit]');
    const visibilityTarget = primaryButton.closest('.product-form') || primaryButton;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries[0]?.isIntersecting;
      bar.hidden = Boolean(visible);
    }, { threshold: 0.2 });
    observer.observe(visibilityTarget);
    stickyButton?.addEventListener('click', () => primaryButton.click());

    const sync = () => {
      if (!stickyButton) return;
      stickyButton.disabled = primaryButton.disabled;
      const mainLabel = primaryButton.querySelector('span:not(.sold-out-message)')?.textContent?.trim();
      if (mainLabel) stickyButton.textContent = mainLabel;
      const price = main.querySelector('[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular')?.textContent?.trim();
      const priceTarget = bar.querySelector('[data-bigfan-sticky-price]');
      if (price && priceTarget) priceTarget.textContent = price;
    };
    new MutationObserver(sync).observe(main, { subtree: true, childList: true, attributes: true, characterData: true });
    sync();
  };

  class BigFanInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.mode = 'auto';
      this.grid = document.querySelector('#product-grid');
      this.nextUrl = container.dataset.nextUrl || '';
      this.loading = false;
      this.done = !this.nextUrl;
      this.page = Number(container.dataset.currentPage || 1);
      this.maxAutoPages = Number(container.dataset.maxAutoPages || 3);
      this.autoPagesLoaded = 0;
      this.sectionId = this.grid?.dataset.id || '';
      this.pageObserver = null;
      this.retryCount = 0;
      this.maxRetries = 3;
      this.proximityRaf = 0;
      this.proximityDelay = 0;
      this.button = container.querySelector('[data-bigfan-load-more]');
      this.status = container.querySelector('[data-bigfan-infinite-status]');
      this.sentinel = container.querySelector('[data-bigfan-infinite-sentinel]');
      this.collectionKey = this.buildStateKey();
      this.shouldRestore = false;
      this.navigatingToProduct = false;
      this.returnTarget = null;
      this.backToGridButton = null;
      document.documentElement.classList.add('bigfan-infinite-enhanced');
      this.createBackToGridButton();
      this.bind();
      this.initPageHistory();
      this.restore().finally(() => {
        document.documentElement.classList.remove('bigfan-restore-pending');
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
        this.observe();
        this.scheduleProximityCheck();
        this.updateBackToGridButton();
      });
    }

    buildStateKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return COLLECTION_STATE_PREFIX + url.pathname + '?' + url.searchParams.toString();
    }

    initPageHistory() {
      if (!this.grid || !('IntersectionObserver' in window) || !('history' in window)) return;

      this.pageObserver = new IntersectionObserver((entries) => {
        if (this.navigatingToProduct || document.documentElement.classList.contains('bigfan-product-overlay-open')) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const marker = visible.target;
        const pageUrl = marker.dataset.bigfanPageUrl;
        const pageNumber = Number(marker.dataset.bigfanPage || 1);
        if (!pageUrl || !pageNumber) return;

        try {
          const target = new URL(pageUrl, window.location.origin);
          const current = new URL(window.location.href);
          if (target.pathname === current.pathname && target.search === current.search) return;
          history.replaceState({ ...(history.state || {}), bigfanPage: pageNumber }, '', `${target.pathname}${target.search}${target.hash}`);
        } catch (_) {}
      }, { rootMargin: '-22% 0px -68% 0px', threshold: [0, 0.01, 0.25, 0.5] });

      const firstItem = this.grid.firstElementChild;
      if (firstItem) {
        firstItem.dataset.bigfanPage = String(this.page);
        firstItem.dataset.bigfanPageUrl = window.location.href;
        this.pageObserver.observe(firstItem);
      }
    }

    observePageStart(item, pageNumber, pageUrl) {
      if (!item || !this.pageObserver) return;
      item.dataset.bigfanPage = String(pageNumber);
      item.dataset.bigfanPageUrl = pageUrl;
      this.pageObserver.observe(item);
    }

    createBackToGridButton() {
      // v1.7.31: retire the legacy large floating arrow. One control only.
      document.querySelectorAll('[data-bigfan-back-to-grid]').forEach((node) => node.remove());
      this.backToGridButton = null;
    }

    updateBackToGridButton() {
      // Defensive cleanup for stale markup injected by an older cached asset.
      document.querySelectorAll('[data-bigfan-back-to-grid]').forEach((node) => node.remove());
      this.backToGridButton = null;
    }

    productPathFromLink(link) {
      if (!link?.href) return '';
      try {
        const url = new URL(link.href, window.location.origin);
        return url.pathname.replace(/\/$/, '');
      } catch (_) {
        return '';
      }
    }

    findProductCard(productPath) {
      if (!this.grid || !productPath) return null;
      const links = this.grid.querySelectorAll('a[href*="/products/"]');
      for (const link of links) {
        if (this.productPathFromLink(link) === productPath) {
          return link.closest('.grid__item') || link.closest('.product-card-wrapper') || link;
        }
      }
      return null;
    }

    normalizeReturnHistoryUrl() {
      try {
        const current = new URL(window.location.href);
        if (!current.searchParams.has('page')) return;
        current.searchParams.delete('page');
        const normalized = `${current.pathname}${current.search}${current.hash}`;
        history.replaceState({ ...(history.state || {}), bigfanReturnBase: true }, '', normalized);
      } catch (_) {}
    }

    bind() {
      this.button?.addEventListener('click', () => this.loadNext(false));
      this.onViewportChange = () => {
        this.scheduleProximityCheck();
        this.updateBackToGridButton();
      };
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.grid?.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="/products/"]');
        if (event.defaultPrevented || !link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
        const card = link.closest('.grid__item') || link.closest('.product-card-wrapper');
        this.returnTarget = {
          productPath: this.productPathFromLink(link),
          viewportTop: card ? card.getBoundingClientRect().top : null
        };
        this.shouldRestore = true;
        this.navigatingToProduct = true;
        this.saveState();
        // The visible-page URL is useful while scrolling, but returning from a
        // product must start from page 1 so pages 2..N can be rebuilt without
        // the browser first painting a short page at its bottom edge.
        this.normalizeReturnHistoryUrl();
      });
      window.addEventListener('pagehide', () => {
        if (this.shouldRestore) this.saveState();
      });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.restore().finally(() => {
          document.documentElement.classList.remove('bigfan-restore-pending');
          if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
          this.observe();
          this.scheduleProximityCheck();
          this.updateBackToGridButton();
        });
      });
    }

    saveState() {
      if (!this.grid) return;
      try {
        sessionStorage.setItem(this.collectionKey, JSON.stringify({
          pages: this.page,
          scrollY: window.scrollY,
          productPath: this.returnTarget?.productPath || '',
          viewportTop: Number.isFinite(this.returnTarget?.viewportTop) ? this.returnTarget.viewportTop : null,
          at: Date.now()
        }));
      } catch (_) {}
    }

    async restore() {
      let state;
      try { state = parseJSON(sessionStorage.getItem(this.collectionKey), null); } catch (_) { state = null; }
      if (!state) return;
      if (Date.now() - state.at > 30 * 60 * 1000) {
        try { sessionStorage.removeItem(this.collectionKey); } catch (_) {}
        return;
      }

      const targetPage = Math.max(Number(state.pages || 1), this.page);
      if (this.page < targetPage && !this.done) this.setStatus('Restaurando tu posición…', true);
      while (!this.done && this.page < targetPage) {
        await this.loadNext(true);
      }

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const card = this.findProductCard(state.productPath || '');
      if (card) {
        const cardTop = card.getBoundingClientRect().top + window.scrollY;
        const desiredViewportTop = Number.isFinite(state.viewportTop)
          ? Math.max(90, Math.min(state.viewportTop, (window.innerHeight || 900) * 0.68))
          : Math.min(180, (window.innerHeight || 900) * 0.2);
        window.scrollTo({ top: Math.max(0, cardTop - desiredViewportTop), behavior: 'auto' });
        card.classList.add('bigfan-return-highlight');
        window.setTimeout(() => card.classList.remove('bigfan-return-highlight'), 1800);
      } else {
        console.warn('[BigFan] Could not locate the previously selected product; restoring saved scroll position instead.', state.productPath || '(no product path)');
        window.scrollTo({ top: state.scrollY || 0, behavior: 'auto' });
      }

      this.setStatus(this.done ? 'Has visto todos los productos disponibles.' : '');
      try { sessionStorage.removeItem(this.collectionKey); } catch (_) {}
      this.shouldRestore = false;
      this.navigatingToProduct = false;
      this.returnTarget = null;
      this.updateBackToGridButton();
    }

    scheduleProximityCheck(delay = 0) {
      if (this.done || this.mode === 'button') return;
      if (delay > 0) {
        window.clearTimeout(this.proximityDelay);
        this.proximityDelay = window.setTimeout(() => this.scheduleProximityCheck(), delay);
        return;
      }
      if (this.proximityRaf) return;
      this.proximityRaf = window.requestAnimationFrame(() => {
        this.proximityRaf = 0;
        this.checkProximity();
      });
    }

    isNearLoadBoundary() {
      if (!this.sentinel) return false;
      const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      const preload = Math.max(1800, viewport * 1.75);
      const rect = this.sentinel.getBoundingClientRect();
      const documentBottomDistance = Math.max(0, document.documentElement.scrollHeight - (window.scrollY + viewport));
      return rect.top <= viewport + preload || documentBottomDistance <= preload;
    }

    checkProximity() {
      if (this.loading || this.done || !this.nextUrl || this.mode === 'button') return;
      if (this.mode === 'hybrid' && this.autoPagesLoaded >= this.maxAutoPages) {
        if (this.button) this.button.hidden = false;
        return;
      }
      if (this.isNearLoadBoundary()) this.loadNext(false);
    }

    observe() {
      if (this.done || this.mode === 'button' || !this.sentinel) return;
      this.observer?.disconnect();
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || this.loading || this.done) return;
        if (this.mode === 'hybrid' && this.autoPagesLoaded >= this.maxAutoPages) {
          if (this.button) this.button.hidden = false;
          this.observer.disconnect();
          return;
        }
        this.loadNext(false);
      }, { rootMargin: '2200px 0px 2200px 0px', threshold: 0 });
      this.observer.observe(this.sentinel);
      this.scheduleProximityCheck();
    }

    setStatus(text, busy = false) {
      if (!this.status) return;
      this.status.innerHTML = busy ? `<span class="bigfan-infinite__spinner" aria-hidden="true"></span><span>${text}</span>` : `<span>${text}</span>`;
      this.status.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    async loadNext(restoring = false) {
      if (this.loading || this.done || !this.nextUrl || !this.grid) return;
      this.loading = true;
      this.button && (this.button.disabled = true);
      this.setStatus('Cargando más productos…', true);
      try {
        let appendedCount = 0;
        let fetchedPages = 0;
        let safety = 0;

        while (!this.done && this.nextUrl && appendedCount === 0 && safety < 8) {
          safety += 1;
          const publicPageUrl = new URL(this.nextUrl, window.location.origin);
          const loadedPage = Number(publicPageUrl.searchParams.get('page') || (this.page + 1));
          const fetchUrl = new URL(publicPageUrl);
          if (this.sectionId) fetchUrl.searchParams.set('section_id', this.sectionId);

          let response = await fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          let html = await response.text();
          let doc = new DOMParser().parseFromString(html, 'text/html');
          let sourceGrid = doc.querySelector('#product-grid');

          // Some Shopify render paths can return a section wrapper that does not
          // contain the expected grid. Retry the canonical paginated URL once so
          // automatic loading never depends on one rendering mode only.
          if (!sourceGrid && this.sectionId) {
            response = await fetch(publicPageUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            html = await response.text();
            doc = new DOMParser().parseFromString(html, 'text/html');
            sourceGrid = doc.querySelector('#product-grid');
          }

          if (!sourceGrid) throw new Error('No product grid found');

          const sourceItems = [...sourceGrid.children];
          appendedCount += sourceItems.length;
          const fragment = document.createDocumentFragment();
          sourceItems.forEach((item) => fragment.appendChild(item));
          this.grid.appendChild(fragment);
          if (sourceItems.length) {
            this.observePageStart(sourceItems[0], loadedPage, publicPageUrl.toString());
            initFavorites(this.grid);
            initProductCardGalleries(this.grid);
          }

          const nextContainer = doc.querySelector('[data-bigfan-infinite]');
          this.nextUrl = nextContainer?.dataset.nextUrl || '';
          this.page = Number(nextContainer?.dataset.currentPage || loadedPage);
          this.done = !this.nextUrl;
          fetchedPages += 1;
          this.container.dataset.nextUrl = this.nextUrl;
          this.container.dataset.currentPage = String(this.page);
        }

        if (appendedCount > 0) this.retryCount = 0;
        if (!restoring && appendedCount > 0) this.autoPagesLoaded += 1;
        this.setStatus(this.done ? 'Has visto todos los productos disponibles.' : '');

        if (this.done) {
          if (this.button) this.button.hidden = true;
          this.observer?.disconnect();
        } else if (this.mode === 'button') {
          if (this.button) this.button.hidden = false;
        } else if (this.mode === 'hybrid' && this.autoPagesLoaded >= this.maxAutoPages) {
          if (this.button) this.button.hidden = false;
          this.observer?.disconnect();
        }

        if (appendedCount === 0 && !this.done && safety >= 8) {
          throw new Error(`No visible products found after ${fetchedPages} pages`);
        }
      } catch (error) {
        this.retryCount += 1;
        console.warn(`[BigFan] Infinite scroll attempt ${this.retryCount} failed.`, error);
        if (this.retryCount <= this.maxRetries && this.nextUrl) {
          this.setStatus('Reintentando cargar más productos…', true);
          this.scheduleProximityCheck(500 * this.retryCount);
        } else {
          document.documentElement.classList.remove('bigfan-infinite-enhanced');
          this.container.closest('.collection')?.querySelector('.bigfan-pagination-fallback')?.removeAttribute('data-hide-when-js');
          this.setStatus('No se pudieron cargar más productos automáticamente. Usa la paginación.', false);
          if (this.button) this.button.hidden = true;
          this.observer?.disconnect();
        }
      } finally {
        this.loading = false;
        if (this.button) this.button.disabled = false;
        if (!this.done && this.nextUrl && this.retryCount <= this.maxRetries) {
          this.scheduleProximityCheck(80);
          this.scheduleProximityCheck(450);
        }
      }
    }
  }

  const initInfiniteScroll = (root = document) => {
    if (document.documentElement.dataset.bigfanStockBootstrap === 'pending') return;
    root.querySelectorAll('[data-bigfan-infinite]').forEach((container) => {
      if (container.dataset.bigfanBound === 'true') return;
      container.dataset.bigfanBound = 'true';
      new BigFanInfiniteScroll(container);
    });
  };


  const initProductCardGalleries = (root = document) => {
    root.querySelectorAll('[data-bigfan-card-gallery]').forEach((gallery) => {
      if (gallery.dataset.bigfanGalleryBound === 'true') return;
      gallery.dataset.bigfanGalleryBound = 'true';

      const viewport = gallery.querySelector('[data-bigfan-card-gallery-viewport]');
      const track = gallery.querySelector('[data-bigfan-card-gallery-track]');
      const template = gallery.querySelector('[data-bigfan-card-gallery-template]');
      const prev = gallery.querySelector('[data-bigfan-card-gallery-prev]');
      const next = gallery.querySelector('[data-bigfan-card-gallery-next]');
      const current = gallery.querySelector('[data-bigfan-card-gallery-current]');
      const allDots = Array.from(gallery.querySelectorAll('[data-bigfan-card-gallery-dot]'));
      const dots = allDots.slice(0, 5);
      allDots.slice(5).forEach((dot) => { dot.hidden = true; dot.setAttribute('aria-hidden', 'true'); });
      const total = Math.max(1, Number(gallery.dataset.galleryCount || 1));
      if (!viewport || !track || !template || total < 2) return;

      let index = 0;
      let pointerStartX = 0;
      let pointerStartY = 0;
      let pointerActive = false;
      let suppressClickUntil = 0;

      const hydrateImage = (image) => {
        if (!image || image.dataset.bigfanGalleryHydrated === 'true') return image;
        image.dataset.bigfanGalleryHydrated = 'true';
        const srcset = image.dataset.srcset;
        const src = image.dataset.src;
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
        return image;
      };

      const ensureSlide = (targetIndex) => {
        if (targetIndex < 0 || targetIndex >= total) return null;
        let slide = track.querySelector(`[data-gallery-index="${targetIndex}"]`);
        if (slide) return hydrateImage(slide);

        const source = template.content.querySelector(`[data-gallery-index="${targetIndex}"]`);
        if (!source) return null;
        slide = source.cloneNode(true);
        hydrateImage(slide);
        track.appendChild(slide);
        return slide;
      };

      const setLoadingState = (image) => {
        if (!image || image.complete) {
          gallery.classList.remove('is-loading');
          return;
        }
        gallery.classList.add('is-loading');
        const done = () => gallery.classList.remove('is-loading');
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      };

      const getVisibleActiveDotIndex = () => {
        const visibleDotCount = dots.length || 0;
        if (visibleDotCount <= 1 || total <= visibleDotCount) return index;
        const trailingSlots = visibleDotCount - 2;
        if (index <= 1) return index;
        if (index >= total - trailingSlots) return visibleDotCount - (total - index);
        return Math.floor(visibleDotCount / 2);
      };

      const updateControls = () => {
        if (current) current.textContent = `Imagen ${index + 1} de ${total}`;
        const activeDotIndex = getVisibleActiveDotIndex();
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === activeDotIndex);
        });
        prev?.removeAttribute('disabled');
        next?.removeAttribute('disabled');
        track.querySelectorAll('[data-bigfan-card-gallery-image]').forEach((image) => {
          const active = Number(image.dataset.galleryIndex || 0) === index;
          image.classList.toggle('is-active', active);
          image.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      };

      const preloadFollowing = () => {
        const followingIndex = (index + 1) % total;
        const run = () => ensureSlide(followingIndex);
        if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 900 });
        else window.setTimeout(run, 180);
      };

      const goTo = (targetIndex) => {
        const nextIndex = ((targetIndex % total) + total) % total;
        if (nextIndex === index) {
          updateControls();
          return;
        }

        // The track translates by the real gallery index. When wrapping from
        // the first image directly to the last, materialize the intermediate
        // slots first so the translated position always maps to a real slide.
        for (let slideIndex = 1; slideIndex <= nextIndex; slideIndex += 1) {
          ensureSlide(slideIndex);
        }
        const image = ensureSlide(nextIndex);
        if (!image) return;
        index = nextIndex;
        setLoadingState(image);
        track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
        updateControls();
        preloadFollowing();
      };

      [prev, next].forEach((button) => {
        button?.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      });

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        goTo(index - 1);
      });

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        goTo(index + 1);
      });

      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(index + 1);
        }
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        pointerActive = true;
        ensureSlide((index + 1) % total);
      }, { passive: true });

      viewport.addEventListener('pointerup', (event) => {
        if (!pointerActive || event.pointerType === 'mouse') return;
        pointerActive = false;
        const dx = event.clientX - pointerStartX;
        const dy = event.clientY - pointerStartY;
        if (Math.abs(dx) < 34 || Math.abs(dx) <= Math.abs(dy) * 1.12) return;
        suppressClickUntil = Date.now() + 450;
        if (dx < 0) goTo(index + 1);
        else goTo(index - 1);
      }, { passive: true });

      viewport.addEventListener('pointercancel', () => {
        pointerActive = false;
      }, { passive: true });

      viewport.addEventListener('click', (event) => {
        if (Date.now() >= suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      });

      // Preload only the second image after clear user intent. Remaining images
      // stay inert inside <template> until the shopper actually navigates them.
      const warmSecondImage = () => ensureSlide(1);
      gallery.addEventListener('mouseenter', warmSecondImage, { once: true });
      gallery.addEventListener('focusin', warmSecondImage, { once: true });

      updateControls();
    });
  };


  /* --------------------------------------------------------------------------
     BigFan product overlay
     Keeps collection/home DOM alive while displaying the real Shopify product
     section. Real product <a href> links remain intact for SEO/no-JS fallback.
     -------------------------------------------------------------------------- */
  class BigFanProductOverlayController {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-bigfan-product-overlay-panel]');
      this.body = root.querySelector('[data-bigfan-product-overlay-body]');
      this.content = root.querySelector('[data-bigfan-product-overlay-content]');
      this.loading = root.querySelector('[data-bigfan-product-overlay-loading]');
      this.fullLink = root.querySelector('[data-bigfan-product-overlay-full-link]');
      this.closeButton = root.querySelector('.bigfan-product-overlay__close');
      this.cache = new Map();
      this.activeUrl = '';
      this.trigger = null;
      this.baseTitle = document.title;
      this.sequence = 0;
      this.openToken = 0;
      this.isOpen = false;
      this.bound = false;
      this.prefetchTimer = 0;
      this.originalBodyPaddingRight = '';
    }

    init() {
      if (this.bound || !this.panel || !this.content) return;
      this.bound = true;

      document.addEventListener('click', (event) => this.onDocumentClick(event), true);
      document.addEventListener('pointerover', (event) => this.onIntent(event), { passive: true });
      document.addEventListener('focusin', (event) => this.onIntent(event));
      document.addEventListener('keydown', (event) => this.onKeydown(event));
      window.addEventListener('popstate', (event) => this.onPopState(event));
      this.root.addEventListener('product-info:loaded', (event) => this.onProductInfoLoaded(event));

      this.root.querySelectorAll('[data-bigfan-product-overlay-close]').forEach((control) => {
        control.addEventListener('click', () => this.requestClose());
      });

      this.bindMobileSwipeBack();
    }

    bindMobileSwipeBack() {
      if (this.root.dataset.bigfanSwipeBackBound === 'true') return;
      this.root.dataset.bigfanSwipeBackBound = 'true';

      let startX = null;
      let startY = null;
      let startTime = 0;
      let startTarget = null;

      const reset = () => {
        startX = null;
        startY = null;
        startTime = 0;
        startTarget = null;
      };

      const isBlockedTarget = (target) => {
        if (!(target instanceof Element)) return false;
        return Boolean(target.closest(
          'button, input, select, textarea, summary, [role="button"], product-modal, modal-dialog, media-gallery, slider-component, .product__media-wrapper, .thumbnail-slider, .slider, .swiper, .splide'
        ));
      };

      this.panel.addEventListener('touchstart', (event) => {
        if (!this.isOpen || !window.matchMedia('(max-width: 749px)').matches) return;
        if (event.touches.length !== 1) return reset();
        if (this.root.querySelector('product-modal[open], modal-dialog[open]')) return reset();

        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startTime = Date.now();
        startTarget = event.target;
      }, { passive: true });

      this.panel.addEventListener('touchend', (event) => {
        if (startX == null || startY == null || !this.isOpen) return reset();
        if (!window.matchMedia('(max-width: 749px)').matches) return reset();
        if (isBlockedTarget(startTarget)) return reset();

        const touch = event.changedTouches?.[0];
        if (!touch) return reset();

        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        const elapsed = Date.now() - startTime;
        const horizontal = dx >= 85 && Math.abs(dx) > Math.abs(dy) * 1.2;
        const deliberate = elapsed <= 900;

        reset();
        if (!horizontal || !deliberate) return;
        this.requestClose();
      }, { passive: true });

      this.panel.addEventListener('touchcancel', reset, { passive: true });
    }

    getProductLink(target) {
      if (!(target instanceof Element)) return null;
      const link = target.closest('a[href*="/products/"]');
      if (!link || !link.closest('.product-card-wrapper')) return null;
      if (document.querySelector('#MainContent')?.dataset.template === 'product') return null;
      return link;
    }

    isEligibleClick(event, link) {
      if (!link || event.defaultPrevented) return false;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
      if (link.target === '_blank' || link.hasAttribute('download')) return false;
      if (event.target.closest('button, input, select, textarea, summary, [role="button"]')) return false;
      try {
        const url = new URL(link.href, window.location.origin);
        return url.origin === window.location.origin && url.pathname.includes('/products/');
      } catch (_) {
        return false;
      }
    }

    onDocumentClick(event) {
      const link = this.getProductLink(event.target);
      if (!this.isEligibleClick(event, link)) return;

      // Progressive enhancement: the real href remains present and is used if
      // anything below fails. Prevent navigation only for the enhanced path.
      event.preventDefault();
      const targetUrl = link.href;
      this.open(targetUrl, { trigger: link, pushHistory: true }).catch(() => {
        if (this.isOpen && this.activeUrl === this.normalizeUrl(targetUrl)) window.location.assign(targetUrl);
      });
    }

    onIntent(event) {
      const link = this.getProductLink(event.target);
      if (!link || this.isOpen) return;
      window.clearTimeout(this.prefetchTimer);
      this.prefetchTimer = window.setTimeout(() => {
        this.fetchProduct(link.href).catch(() => {});
      }, event.type === 'focusin' ? 0 : 140);
    }

    onKeydown(event) {
      if (!this.isOpen || event.key !== 'Escape') return;
      if (this.root.querySelector('product-modal[open], modal-dialog[open]')) return;
      event.preventDefault();
      this.requestClose();
    }

    onProductInfoLoaded(event) {
      const target = event.target;
      if (!target?.matches?.('product-info') || !this.content.contains(target)) return;
      const originalSection = target.dataset.originalSection;
      const overlaySection = target.dataset.section;
      if (!originalSection || !overlaySection || typeof target.addPreProcessCallback !== 'function') return;

      target.addPreProcessCallback((html) => {
        if (!html) return;
        html.innerHTML = html.innerHTML.replaceAll(originalSection, overlaySection);
        Array.from(html.attributes || []).forEach((attribute) => {
          if (attribute.value.includes(originalSection)) {
            html.setAttribute(attribute.name, attribute.value.replaceAll(originalSection, overlaySection));
          }
        });
        html.dataset.originalSection = originalSection;
        html.dataset.updateUrl = 'false';
        html.classList.add('bigfan-product-overlay__product-info');
        html.querySelectorAll('.scroll-trigger').forEach((node) => {
          node.classList.add('scroll-trigger--cancel');
          node.classList.remove('animate--slide-in');
        });
      });
    }

    onPopState(event) {
      const state = event.state || {};
      if (state.bigfanProductOverlay && state.bigfanProductUrl) {
        if (!this.isOpen || this.activeUrl !== state.bigfanProductUrl) {
          this.open(state.bigfanProductUrl, { pushHistory: false, fromHistory: true }).catch(() => {
            window.location.assign(state.bigfanProductUrl);
          });
        }
        return;
      }
      if (this.isOpen) this.hide({ restoreFocus: true });
    }

    requestClose() {
      if (!this.isOpen) return;
      if (history.state?.bigfanProductOverlay) {
        history.back();
      } else {
        this.hide({ restoreFocus: true });
      }
    }

    normalizeUrl(rawUrl) {
      const url = new URL(rawUrl, window.location.origin);
      url.hash = '';
      return `${url.pathname}${url.search}`;
    }

    async fetchProduct(rawUrl) {
      const url = new URL(rawUrl, window.location.origin);
      url.hash = '';
      const cacheKey = `${url.pathname}${url.search}`;
      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

      const promise = fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const productInfo = doc.querySelector('product-info');
        if (!productInfo) throw new Error('Product page did not contain product-info');
        return {
          html: productInfo.outerHTML,
          title: doc.querySelector('title')?.textContent?.trim() || document.title
        };
      }).catch((error) => {
        this.cache.delete(cacheKey);
        throw error;
      });

      this.cache.set(cacheKey, promise);
      return promise;
    }

    prepareProductElement(rawHtml) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = rawHtml;
      const productElement = wrapper.querySelector('product-info');
      if (!productElement) return null;

      productElement.querySelectorAll('.scroll-trigger').forEach((node) => {
        node.classList.add('scroll-trigger--cancel');
        node.classList.remove('animate--slide-in');
      });
      productElement.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());

      const originalSection = productElement.dataset.section;
      if (originalSection) {
        const newSection = `bigfan-overlay-${++this.sequence}-${originalSection}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(originalSection, newSection);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(originalSection)) {
            productElement.setAttribute(attribute.name, attribute.value.replaceAll(originalSection, newSection));
          }
        });
        productElement.dataset.originalSection = originalSection;
      }

      productElement.dataset.updateUrl = 'false';
      productElement.classList.add('bigfan-product-overlay__product-info');
      return productElement;
    }

    lockBackground() {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      this.originalBodyPaddingRight = document.body.style.paddingRight || '';
      document.documentElement.style.setProperty('--bigfan-overlay-scrollbar', `${scrollbarWidth}px`);
      document.documentElement.classList.add('bigfan-product-overlay-open');
    }

    unlockBackground() {
      document.documentElement.classList.remove('bigfan-product-overlay-open');
      document.documentElement.style.removeProperty('--bigfan-overlay-scrollbar');
      document.body.style.paddingRight = this.originalBodyPaddingRight;
    }

    setLoading(isLoading) {
      if (this.loading) this.loading.hidden = !isLoading;
      this.root.classList.toggle('is-loading', isLoading);
    }

    async open(rawUrl, { trigger = null, pushHistory = true } = {}) {
      const normalizedUrl = this.normalizeUrl(rawUrl);
      const token = ++this.openToken;
      this.activeUrl = normalizedUrl;
      this.trigger = trigger || this.trigger;

      if (!this.isOpen) {
        this.baseTitle = document.title;
        this.root.hidden = false;
        this.root.setAttribute('aria-hidden', 'false');
        this.root.classList.add('is-open');
        this.isOpen = true;
        this.lockBackground();
        this.body.scrollTop = 0;
        this.content.innerHTML = '';
        this.setLoading(true);
        requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
      } else {
        this.body.scrollTop = 0;
        this.content.innerHTML = '';
        this.setLoading(true);
      }

      if (this.fullLink) this.fullLink.href = normalizedUrl;

      if (pushHistory) {
        history.pushState(
          { ...(history.state || {}), bigfanProductOverlay: true, bigfanProductUrl: normalizedUrl },
          '',
          normalizedUrl
        );
      }

      const result = await this.fetchProduct(normalizedUrl);
      if (token !== this.openToken || !this.isOpen) return;
      const productElement = this.prepareProductElement(result.html);
      if (!productElement) throw new Error('Could not prepare product overlay');

      HTMLUpdateUtility.setInnerHTML(this.content, productElement.outerHTML);
      this.setLoading(false);
      document.title = result.title || this.baseTitle;

      initFavorites(this.content);
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      if (window.ProductModel) window.ProductModel.loadShopifyXR();
      this.content.querySelector('product-component')?.dispatchViewEvent?.();

      requestAnimationFrame(() => {
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.closeButton || this.panel);
      });
    }

    hide({ restoreFocus = true } = {}) {
      ++this.openToken;
      this.isOpen = false;
      this.root.classList.remove('is-open', 'is-loading');
      this.root.hidden = true;
      this.root.setAttribute('aria-hidden', 'true');
      this.content.innerHTML = '';
      this.setLoading(false);
      this.unlockBackground();
      document.title = this.baseTitle;
      this.activeUrl = '';

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(restoreFocus && this.trigger?.isConnected ? this.trigger : null);
      } else if (restoreFocus && this.trigger?.isConnected) {
        this.trigger.focus({ preventScroll: true });
      }
    }
  }

  let productOverlayController = null;
  const initProductOverlay = () => {
    if (productOverlayController) return productOverlayController;
    const root = document.querySelector('#BigFanProductOverlay');
    if (!root) return null;
    productOverlayController = new BigFanProductOverlayController(root);
    productOverlayController.init();
    return productOverlayController;
  };



  const initCatalogTabs = (root = document) => {
    root.querySelectorAll('[data-bigfan-catalog-tabs]').forEach((catalog) => {
      if (catalog.dataset.bigfanCatalogBound === 'true') return;
      catalog.dataset.bigfanCatalogBound = 'true';

      const tabs = [...catalog.querySelectorAll('[data-bigfan-catalog-tab]')];
      const panels = [...catalog.querySelectorAll('[data-bigfan-catalog-panel]')];
      if (!tabs.length || !panels.length) return;

      const activate = (tab, moveFocus = false) => {
        const targetId = tab.dataset.bigfanCatalogTab;
        const target = panels.find((panel) => panel.id === targetId);
        if (!target) return;

        const activePanel = panels.find((panel) => !panel.hidden);
        const panelChanged = activePanel && activePanel !== target;

        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });

        panels.forEach((panel) => {
          const active = panel === target;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });

        if (panelChanged) {
          const scrollContainer = catalog.querySelector('.bigfan-catalog-desktop__content');
          if (scrollContainer) scrollContainer.scrollTop = 0;
        }

        if (moveFocus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('mouseenter', () => activate(tab));
        tab.addEventListener('focus', () => activate(tab));
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(tabs[nextIndex], true);
        });
      });
    });
  };

  const initCatalogHoverMenus = (root = document) => {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    root
      .querySelectorAll('header-menu > details.bigfan-mega-menu--catalog-sync, header-menu > details.bigfan-dropdown-menu--catalog-sync')
      .forEach((details) => {
        if (details.dataset.bigfanCatalogHoverBound === 'true') return;
        details.dataset.bigfanCatalogHoverBound = 'true';

        const host = details.closest('header-menu');
        const summary = details.querySelector(':scope > summary');
        if (!host || !summary) return;

        let closeTimer = null;

        const openMenu = () => {
          if (!finePointer.matches) return;
          if (closeTimer) {
            window.clearTimeout(closeTimer);
            closeTimer = null;
          }

          document.querySelectorAll('header-menu > details[open]').forEach((other) => {
            if (other === details) return;
            other.removeAttribute('open');
            const otherSummary = other.querySelector(':scope > summary');
            if (otherSummary) otherSummary.setAttribute('aria-expanded', 'false');
          });

          details.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        };

        const scheduleClose = () => {
          if (!finePointer.matches) return;
          if (closeTimer) window.clearTimeout(closeTimer);
          closeTimer = window.setTimeout(() => {
            details.removeAttribute('open');
            summary.setAttribute('aria-expanded', 'false');
            closeTimer = null;
          }, 140);
        };

        host.addEventListener('mouseenter', openMenu);
        host.addEventListener('mouseleave', scheduleClose);
        summary.addEventListener('focus', openMenu);

        // A mouse click must not immediately close a menu that was opened by hover.
        // Keyboard activation and touch keep the native <details> behaviour.
        summary.addEventListener('click', (event) => {
          if (!finePointer.matches || event.detail === 0) return;
          event.preventDefault();
          openMenu();
        });

        details.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          summary.focus();
        });
      });
  };

  const initProductMediaGalleryNavigation = (root = document) => {
    root.querySelectorAll('[data-bigfan-product-gallery-nav]').forEach((nav) => {
      if (nav.dataset.bigfanGalleryNavBound === 'true') return;
      nav.dataset.bigfanGalleryNavBound = 'true';

      const gallery = nav.closest('media-gallery');
      const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
      const prev = nav.querySelector('[data-bigfan-product-gallery-prev]');
      const next = nav.querySelector('[data-bigfan-product-gallery-next]');
      if (!gallery || !viewer || !prev || !next) return;

      // Only the top-level gallery slides are navigation targets. Product media
      // contains nested elements with their own data-media-id attributes; treating
      // those as slides removes `is-active` from every real slide and collapses
      // the main viewer on desktop.
      const mediaList = viewer.querySelector('[id^="Slider-Gallery"]');
      const getItems = () => Array.from(mediaList?.children || []).filter((item) =>
        item.matches?.('.product__media-item[data-media-id]')
      );
      const getActiveIndex = (items) => {
        const currentThumb = gallery.querySelector('[id^="GalleryThumbnails"] button[aria-current="true"]')?.closest('[data-target]');
        if (currentThumb?.dataset.target) {
          const thumbIndex = items.findIndex((item) => item.dataset.mediaId === currentThumb.dataset.target);
          if (thumbIndex >= 0) return thumbIndex;
        }
        const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
        return activeIndex >= 0 ? activeIndex : 0;
      };

      const syncMediaInsets = () => {
        const items = getItems();
        if (!items.length) return;
        const active = items[getActiveIndex(items)] || items[0];
        const mediaContainer = active.querySelector('.product-media-container') || active;
        const viewerRect = viewer.getBoundingClientRect();
        const mediaRect = mediaContainer.getBoundingClientRect();
        if (!viewerRect.width || !mediaRect.width) return;
        const leftInset = Math.max(0, mediaRect.left - viewerRect.left);
        const rightInset = Math.max(0, viewerRect.right - mediaRect.right);
        gallery.style.setProperty('--bigfan-product-media-left-inset', `${leftInset}px`);
        gallery.style.setProperty('--bigfan-product-media-right-inset', `${rightInset}px`);
      };

      const scheduleInsetSync = () => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(syncMediaInsets));
      };

      const activate = (delta) => {
        const items = getItems();
        if (items.length < 2) return;
        const activeIndex = getActiveIndex(items);
        const targetIndex = (activeIndex + delta + items.length) % items.length;
        const target = items[targetIndex];
        if (!target?.dataset.mediaId) return;

        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(target.dataset.mediaId, false);
        } else {
          const thumbnail = gallery.querySelector(`[data-target="${CSS.escape(target.dataset.mediaId)}"] button`);
          thumbnail?.click();
        }
        scheduleInsetSync();
      };

      [prev, next].forEach((button) => {
        button.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      });

      prev.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        activate(-1);
      });

      next.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        activate(1);
      });

      viewer.addEventListener('slideChanged', scheduleInsetSync);
      gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target] button').forEach((button) => {
        button.addEventListener('click', scheduleInsetSync);
      });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(scheduleInsetSync);
        resizeObserver.observe(viewer);
      } else {
        window.addEventListener('resize', scheduleInsetSync, { passive: true });
      }

      gallery.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', scheduleInsetSync, { once: true });
      });

      scheduleInsetSync();
    });
  };

  const initCollectionToTop = () => {
    const button = document.querySelector('[data-bigfan-collection-to-top]');
    if (!button || button.dataset.bigfanBound === 'true') return;
    button.dataset.bigfanBound = 'true';

    document.querySelectorAll('[data-bigfan-back-to-grid]').forEach((node) => node.remove());

    // This runtime style is appended after the old layout style, so no
    // layout/theme.liquid replacement is needed to undo the "always visible" experiment.
    if (!document.querySelector('[data-bigfan-single-to-top-style]')) {
      const style = document.createElement('style');
      style.dataset.bigfanSingleToTopStyle = 'true';
      style.textContent = `
        @media screen and (max-width: 749px) {
          body.bigfan-theme-enabled .bigfan-back-to-grid { display: none !important; }
          body.bigfan-theme-enabled button.bigfan-collection-to-top::before { content: none !important; display: none !important; }
          body.bigfan-theme-enabled button.bigfan-collection-to-top .bigfan-collection-to-top__icon svg { display: none !important; }
        }
      `;
      document.body.appendChild(style);
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMq = window.matchMedia('(max-width: 749px)');
    let ticking = false;

    const getTarget = () =>
      document.getElementById('ProductGridContainer') || document.getElementById('product-grid');

    const icon = button.querySelector('.bigfan-collection-to-top__icon');
    if (icon) {
      icon.textContent = '↑';
      icon.style.setProperty('display', 'grid', 'important');
      icon.style.setProperty('place-items', 'center', 'important');
      icon.style.setProperty('width', '16px', 'important');
      icon.style.setProperty('height', '16px', 'important');
      icon.style.setProperty('font-size', '16px', 'important');
      icon.style.setProperty('line-height', '1', 'important');
      icon.style.setProperty('transform', 'none', 'important');
      icon.style.setProperty('opacity', '1', 'important');
      icon.style.setProperty('visibility', 'visible', 'important');
    }

    const applyMobileGeometry = () => {
      if (!mobileMq.matches) return;
      button.style.setProperty('position', 'fixed', 'important');
      button.style.setProperty('right', '10px', 'important');
      button.style.setProperty('left', 'auto', 'important');
      button.style.setProperty('top', 'auto', 'important');
      button.style.setProperty('bottom', 'calc(10px + env(safe-area-inset-bottom))', 'important');
      button.style.setProperty('width', '36px', 'important');
      button.style.setProperty('min-width', '36px', 'important');
      button.style.setProperty('max-width', '36px', 'important');
      button.style.setProperty('height', '36px', 'important');
      button.style.setProperty('min-height', '36px', 'important');
      button.style.setProperty('max-height', '36px', 'important');
      button.style.setProperty('padding', '0', 'important');
      button.style.setProperty('box-sizing', 'border-box', 'important');
      button.style.setProperty('z-index', '10070', 'important');
      button.style.setProperty('transform', 'none', 'important');
    };

    const setVisible = (visible) => {
      button.classList.toggle('is-visible', visible);
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      button.tabIndex = visible ? 0 : -1;
      button.style.setProperty('display', 'grid', 'important');
      button.style.setProperty('opacity', visible ? '1' : '0', 'important');
      button.style.setProperty('visibility', visible ? 'visible' : 'hidden', 'important');
      button.style.setProperty('pointer-events', visible ? 'auto' : 'none', 'important');
    };

    const update = () => {
      ticking = false;
      applyMobileGeometry();
      document.querySelectorAll('[data-bigfan-back-to-grid]').forEach((node) => node.remove());

      const target = getTarget();
      if (!target) {
        setVisible(false);
        return;
      }

      const scrollTop = Math.max(
        Number(window.scrollY || window.pageYOffset || 0),
        Number(document.documentElement?.scrollTop || 0),
        Number(document.body?.scrollTop || 0),
      );

      if (mobileMq.matches) {
        const gridTop = target.getBoundingClientRect().top + scrollTop;
        const threshold = gridTop + Math.max(650, (window.innerHeight || 0) * 0.85);
        setVisible(scrollTop >= threshold);
        return;
      }

      setVisible(scrollTop >= 220);
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    button.addEventListener('click', () => {
      const target = getTarget();
      if (!target) return;
      const offset = mobileMq.matches ? 78 : 104;
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
      window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('touchmove', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    window.addEventListener('pageshow', requestUpdate);
    window.addEventListener('load', requestUpdate);
    document.addEventListener('bigfan:infinite-page-appended', requestUpdate);
    if (typeof mobileMq.addEventListener === 'function') mobileMq.addEventListener('change', requestUpdate);

    update();
    [80, 300, 900, 1800].forEach((delay) => window.setTimeout(requestUpdate, delay));
  };

  const initHeaderSearchClear = (root = document) => {
    const queries = root.querySelectorAll?.('[data-bigfan-search-query]:not([data-bigfan-search-clear-bound="true"])') || [];

    queries.forEach((query) => {
      query.dataset.bigfanSearchClearBound = 'true';
      const input = query.querySelector('.bigfan-header-search__input');
      const reset = query.querySelector('.bigfan-header-search__reset');
      const measure = query.querySelector('.bigfan-header-search__measure');
      if (!input || !reset || !measure) return;

      let frame = 0;

      const update = () => {
        frame = 0;
        const value = input.value || '';
        measure.textContent = value;
        reset.classList.toggle('hidden', value.length === 0);

        if (!value) return;

        const inputStyles = window.getComputedStyle(input);
        const queryWidth = query.clientWidth;
        const resetWidth = reset.offsetWidth || 31;
        const paddingLeft = Number.parseFloat(inputStyles.paddingLeft) || 0;
        const textWidth = measure.getBoundingClientRect().width;
        const gap = 9;
        const rightSafety = 4;
        const desiredLeft = paddingLeft + textWidth + gap;
        const maxLeft = Math.max(paddingLeft, queryWidth - resetWidth - rightSafety);
        query.style.setProperty('--bigfan-reset-x', `${Math.min(desiredLeft, maxLeft)}px`);
      };

      const requestUpdate = () => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      };

      input.addEventListener('input', requestUpdate);
      input.addEventListener('change', requestUpdate);
      reset.addEventListener('click', () => window.requestAnimationFrame(update));
      window.addEventListener('resize', requestUpdate, { passive: true });

      if (document.fonts?.ready) document.fonts.ready.then(requestUpdate).catch(() => {});
      requestUpdate();
    });
  };


  const initFilterDrawerHoverOpen = (root = document) => {
    const drawers = root.querySelectorAll?.('.bigfan-filter-drawer:not([data-bigfan-hover-open-bound="true"])') || [];

    drawers.forEach((drawer) => {
      drawer.dataset.bigfanHoverOpenBound = 'true';
      const details = drawer.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary.mobile-facets__open-wrapper');
      const inner = drawer.querySelector('.mobile-facets__inner');
      if (!details || !summary) return;

      let closeTimer = 0;
      const clearCloseTimer = () => {
        if (!closeTimer) return;
        window.clearTimeout(closeTimer);
        closeTimer = 0;
      };

      // Re-use the theme's normal click path so Dawn opens the drawer with all
      // of its focus/scroll handling and our accordion state is restored too.
      const openDrawer = () => {
        clearCloseTimer();
        if (details.open) return;
        summary.click();
      };

      const closeDrawer = () => {
        clearCloseTimer();
        if (!details.open) return;
        if (typeof drawer.closeMenuDrawer === 'function') {
          drawer.closeMenuDrawer(new Event('bigfan-filter-hover-leave'), false);
        } else {
          details.classList.remove('menu-opening');
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }
      };

      const scheduleClose = () => {
        clearCloseTimer();
        closeTimer = window.setTimeout(() => {
          closeTimer = 0;
          const overTrigger = summary.matches(':hover');
          const overPanel = inner?.matches(':hover');
          if (!overTrigger && !overPanel) closeDrawer();
        }, 180);
      };

      // Desktop mouse: hovering the filter button opens the drawer. The short
      // delay on leave lets the pointer travel from the button into the panel.
      summary.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        openDrawer();
      });
      summary.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        scheduleClose();
      });
      inner?.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearCloseTimer();
      });

      // Keyboard focus also opens it. :focus-visible avoids opening on the
      // focus event that precedes a normal mouse click, which would otherwise
      // make that same click immediately toggle the drawer closed again.
      summary.addEventListener('focus', () => {
        window.requestAnimationFrame(() => {
          if (summary.matches(':focus-visible')) openDrawer();
        });
      });
    });
  };

  const initFilterDrawerDismiss = (root = document) => {
    const drawers = root.querySelectorAll?.('.bigfan-filter-drawer:not([data-bigfan-dismiss-bound="true"])') || [];

    const closeDetails = (details) => {
      if (!details?.open) return;
      const drawer = details.closest('.bigfan-filter-drawer');
      const summary = details.querySelector(':scope > summary');

      // Use Dawn's MenuDrawer close method when available so focus trapping,
      // body scroll locking and the closing animation are cleaned up correctly.
      if (drawer && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(new Event('bigfan-filter-dismiss'), summary || false);
        return;
      }

      if (summary) {
        summary.click();
      } else {
        details.classList.remove('menu-opening');
        details.removeAttribute('open');
      }
    };

    drawers.forEach((drawer) => {
      drawer.dataset.bigfanDismissBound = 'true';
      const details = drawer.querySelector(':scope > details');
      const inner = drawer.querySelector('.mobile-facets__inner');
      if (!details) return;

      // Desktop pointer behaviour: as soon as the cursor leaves the actual
      // filter panel, dismiss it. Touch/pens are intentionally ignored.
      inner?.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !details.open) return;
        closeDetails(details);
      });

      // Mobile gesture: drag/swipe the open panel to the LEFT to close it.
      // Pointer events are used first because they are more reliable on modern
      // Android/iOS browsers than waiting for touchend on a scrollable drawer.
      // `touch-action: pan-y` is set in CSS so vertical scrolling remains native
      // while horizontal movement is available to this gesture handler.
      if (inner) {
        let swipePointerId = null;
        let swipeStartX = null;
        let swipeStartY = null;
        let swipeTriggered = false;

        const resetSwipe = () => {
          swipePointerId = null;
          swipeStartX = null;
          swipeStartY = null;
          swipeTriggered = false;
          inner.classList.remove('bigfan-swipe-closing');
        };

        inner.addEventListener('pointerdown', (event) => {
          if (!details.open || event.pointerType === 'mouse' || !event.isPrimary) return;
          swipePointerId = event.pointerId;
          swipeStartX = event.clientX;
          swipeStartY = event.clientY;
          swipeTriggered = false;
        }, { passive: true });

        inner.addEventListener('pointermove', (event) => {
          if (
            swipePointerId == null ||
            event.pointerId !== swipePointerId ||
            swipeStartX == null ||
            swipeStartY == null ||
            !details.open ||
            swipeTriggered
          ) return;

          const deltaX = event.clientX - swipeStartX;
          const deltaY = event.clientY - swipeStartY;
          const horizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

          if (deltaX <= -52 && horizontal) {
            swipeTriggered = true;
            inner.classList.add('bigfan-swipe-closing');
            closeDetails(details);
            window.setTimeout(resetSwipe, 260);
          }
        }, { passive: true });

        inner.addEventListener('pointerup', resetSwipe, { passive: true });
        inner.addEventListener('pointercancel', resetSwipe, { passive: true });

        // Touch fallback for WebViews/browsers that do not deliver pointermove
        // consistently on a scrollable off-canvas panel.
        let touchStartX = null;
        let touchStartY = null;
        inner.addEventListener('touchstart', (event) => {
          if (!details.open || event.touches.length !== 1) return;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
        }, { passive: true });

        inner.addEventListener('touchmove', (event) => {
          if (touchStartX == null || touchStartY == null || !details.open || swipeTriggered) return;
          const touch = event.touches?.[0];
          if (!touch) return;
          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;
          if (deltaX <= -52 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
            swipeTriggered = true;
            inner.classList.add('bigfan-swipe-closing');
            closeDetails(details);
            window.setTimeout(resetSwipe, 260);
          }
        }, { passive: true });

        inner.addEventListener('touchend', () => {
          touchStartX = null;
          touchStartY = null;
          if (!swipeTriggered) resetSwipe();
        }, { passive: true });
        inner.addEventListener('touchcancel', () => {
          touchStartX = null;
          touchStartY = null;
          resetSwipe();
        }, { passive: true });
      }
    });

    // Click / tap anywhere outside the panel or its trigger closes the drawer.
    // This listener lives on document because the off-canvas drawer host itself
    // has zero width in the catalogue layout, so outside clicks do not bubble
    // through the drawer element.
    if (!document.documentElement.dataset.bigfanFilterOutsideBound) {
      document.documentElement.dataset.bigfanFilterOutsideBound = 'true';
      document.addEventListener('pointerdown', (event) => {
        document.querySelectorAll('.bigfan-filter-drawer details[open]').forEach((details) => {
          const drawer = details.closest('.bigfan-filter-drawer');
          if (!drawer) return;
          const target = event.target;
          const clickedPanel = target.closest?.('.mobile-facets__inner');
          const clickedTrigger = target.closest?.('.mobile-facets__open-wrapper');
          if (!clickedPanel && !clickedTrigger) closeDetails(details);
        });
      }, true);
    }

    if (!document.documentElement.dataset.bigfanFilterEscapeBound) {
      document.documentElement.dataset.bigfanFilterEscapeBound = 'true';
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        document.querySelectorAll('.bigfan-filter-drawer details[open]').forEach(closeDetails);
      });
    }
  };


  const initFilterAccordions = (root = document) => {
    const drawers = root.querySelectorAll?.('.bigfan-filter-drawer:not([data-bigfan-accordion-bound="true"])') || [];

    const facetHasValue = (details) => {
      const checkboxes = [...details.querySelectorAll('input[type="checkbox"]')];
      if (checkboxes.length) return checkboxes.some((input) => input.checked);

      const rangeInputs = [...details.querySelectorAll('price-range input, .facets__price input')];
      if (rangeInputs.length) return rangeInputs.some((input) => String(input.value || '').trim() !== '');

      return details.classList.contains('bigfan-facet-has-value');
    };

    const setAccordionOpen = (details, open) => {
      if (!details) return;
      details.open = Boolean(open);
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    drawers.forEach((drawer) => {
      drawer.dataset.bigfanAccordionBound = 'true';
      const mainDetails = drawer.querySelector(':scope > details');
      const mainSummary = mainDetails?.querySelector(':scope > summary');
      const facetsMain = drawer.querySelector('.mobile-facets__main');

      const syncAccordionState = () => {
        drawer.querySelectorAll('.mobile-facets__details[data-bigfan-accordion="true"]').forEach((details) => {
          const hasValue = facetHasValue(details);
          details.classList.toggle('bigfan-facet-has-value', hasValue);
          setAccordionOpen(details, hasValue);
        });
        facetsMain?.classList.remove('submenu-open');
      };

      // Intercept only nested facet headings. Dawn normally turns these into a
      // second sliding screen; here they behave as ordinary in-place accordions.
      drawer.addEventListener('click', (event) => {
        const summary = event.target.closest?.(
          '.mobile-facets__details[data-bigfan-accordion="true"] > .mobile-facets__summary',
        );
        if (!summary || !drawer.contains(summary)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const details = summary.parentElement;
        setAccordionOpen(details, !details.open);
        facetsMain?.classList.remove('submenu-open');
      }, true);

      // Keep filtered facets open and empty facets closed after each value
      // change, including when the facet HTML is refreshed through AJAX.
      drawer.addEventListener('change', (event) => {
        const details = event.target.closest?.('.mobile-facets__details[data-bigfan-accordion="true"]');
        if (!details) return;
        window.requestAnimationFrame(() => {
          const hasValue = facetHasValue(details);
          details.classList.toggle('bigfan-facet-has-value', hasValue);
          setAccordionOpen(details, hasValue);
        });
      });

      drawer.addEventListener('input', (event) => {
        const details = event.target.closest?.('.mobile-facets__details[data-bigfan-accordion="true"]');
        if (!details || event.target.type === 'checkbox') return;
        window.requestAnimationFrame(() => {
          const hasValue = facetHasValue(details);
          details.classList.toggle('bigfan-facet-has-value', hasValue);
          setAccordionOpen(details, hasValue);
        });
      });

      // closeMenuDrawer() removes nested [open] attributes. Re-apply the
      // default state whenever the filter drawer is opened again.
      mainSummary?.addEventListener('click', () => {
        if (mainDetails?.open) return;
        window.setTimeout(syncAccordionState, 0);
      }, true);

      const wrapper = drawer.querySelector('#FacetsWrapperMobile');
      if (wrapper) {
        const observer = new MutationObserver(() => {
          window.requestAnimationFrame(syncAccordionState);
        });
        observer.observe(wrapper, { childList: true, subtree: true });
      }

      syncAccordionState();
    });
  };

  const initMobileListingControls = (root = document) => {
    const layouts = root.querySelectorAll?.('.bigfan-offcanvas-facets-layout') || [];

    layouts.forEach((layout) => {
      if (layout.dataset.bigfanMobileControlsBound === 'true') return;
      layout.dataset.bigfanMobileControlsBound = 'true';

      const productGrid = layout.querySelector(':scope > #ProductGridContainer, :scope > .product-grid-container');
      const filterHost = layout.querySelector('.bigfan-offcanvas-facets');
      const activeFacets = filterHost?.querySelector('.active-facets-mobile');
      const sectionRoot = layout.closest('collection-component, .template-search') || layout.parentElement;
      const sortBar = sectionRoot?.querySelector('.bigfan-sort-bar');
      if (!productGrid || (!sortBar && !activeFacets)) return;

      const controls = document.createElement('div');
      controls.className = 'bigfan-mobile-listing-controls';
      controls.setAttribute('data-bigfan-mobile-listing-controls', '');
      productGrid.before(controls);

      const sortAnchor = sortBar ? document.createComment('bigfan-sort-anchor') : null;
      const activeAnchor = activeFacets ? document.createComment('bigfan-active-filters-anchor') : null;
      if (sortBar) sortBar.before(sortAnchor);
      if (activeFacets) activeFacets.before(activeAnchor);

      const hasAppliedFilters = () => Boolean(
        activeFacets?.querySelector('.active-facets__button, .active-facets__button--light'),
      );

      const syncAppliedState = () => {
        if (!activeFacets) return;
        activeFacets.classList.toggle('bigfan-mobile-active-filters--empty', !hasAppliedFilters());
      };

      const mountForViewport = () => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (mobile) {
          if (sortBar && sortBar.parentElement !== controls) controls.appendChild(sortBar);
          if (activeFacets && activeFacets.parentElement !== controls) controls.appendChild(activeFacets);
          sortBar?.classList.add('bigfan-mobile-sort-mounted');
          activeFacets?.classList.add('bigfan-mobile-active-filters-mounted');
          controls.classList.add('is-mounted');
          syncAppliedState();
        } else {
          if (sortBar && sortAnchor?.parentNode && sortBar.parentElement === controls) sortAnchor.after(sortBar);
          if (activeFacets && activeAnchor?.parentNode && activeFacets.parentElement === controls) activeAnchor.after(activeFacets);
          sortBar?.classList.remove('bigfan-mobile-sort-mounted');
          activeFacets?.classList.remove('bigfan-mobile-active-filters-mounted');
          controls.classList.remove('is-mounted');
        }
      };

      // Facets.js refreshes the existing active-facets element in place. Watch
      // that node so the row hides when filters are cleared and reappears when
      // a new filter is applied, without needing a page reload.
      if (activeFacets) {
        const observer = new MutationObserver(syncAppliedState);
        observer.observe(activeFacets, { childList: true, subtree: true });
      }

      const media = window.matchMedia('(max-width: 749px)');
      if (typeof media.addEventListener === 'function') media.addEventListener('change', mountForViewport);
      else if (typeof media.addListener === 'function') media.addListener(mountForViewport);

      mountForViewport();
    });
  };



  const bigfanReturnFromProductPage = () => {
    const breadcrumbLinks = Array.from(document.querySelectorAll('.bigfan-breadcrumbs a[href]'));
    const fallbackLink = breadcrumbLinks.length ? breadcrumbLinks[breadcrumbLinks.length - 1] : null;
    const fallbackHref = fallbackLink?.getAttribute('href') || document.referrer || '/collections/all';
    const sameOriginReferrer = document.referrer && (() => {
      try { return new URL(document.referrer).origin === window.location.origin; } catch (e) { return false; }
    })();

    if (window.history.length > 1 && sameOriginReferrer) {
      window.history.back();
      return;
    }

    if (fallbackHref) window.location.href = fallbackHref;
  };

  const initMobileProductSwipeBack = () => {
    const main = document.querySelector('main[data-template="product"]');
    if (!main || window.__bigfanMobileProductSwipeBackBound) return;
    window.__bigfanMobileProductSwipeBackBound = true;

    let startX = null;
    let startY = null;
    let startTime = 0;
    let startTarget = null;

    const reset = () => {
      startX = null;
      startY = null;
      startTime = 0;
      startTarget = null;
    };

    const isBlockedTarget = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(
        'button, input, select, textarea, summary, [role="button"], product-modal, modal-dialog, media-gallery, slider-component, .product__media-wrapper, .thumbnail-slider, .slider, .swiper, .splide'
      ));
    };

    document.addEventListener('touchstart', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (!document.querySelector('main[data-template="product"]')) return;
      if (event.touches.length !== 1) return reset();
      if (document.querySelector('product-modal[open], modal-dialog[open]')) return reset();

      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      startTarget = event.target;
    }, { passive: true });

    document.addEventListener('touchend', (event) => {
      if (startX == null || startY == null) return reset();
      if (!window.matchMedia('(max-width: 749px)').matches) return reset();
      if (!document.querySelector('main[data-template="product"]')) return reset();
      if (isBlockedTarget(startTarget)) return reset();

      const touch = event.changedTouches?.[0];
      if (!touch) return reset();

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const elapsed = Date.now() - startTime;
      const horizontal = dx >= 85 && Math.abs(dx) > Math.abs(dy) * 1.2;
      const deliberate = elapsed <= 900;

      reset();
      if (!horizontal || !deliberate) return;
      bigfanReturnFromProductPage();
    }, { passive: true });

    document.addEventListener('touchcancel', reset, { passive: true });
  };

  const initMobileProductBackButton = (root = document) => {
    const main = document.querySelector('main[data-template="product"]');
    if (!main) return;

    const existing = document.querySelector('[data-bigfan-mobile-product-back]');
    if (existing) return;

    const anchor =
      document.querySelector('.bigfan-breadcrumbs-section') ||
      document.querySelector('.bigfan-breadcrumbs') ||
      main.firstElementChild;
    if (!anchor) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bigfan-mobile-product-back';
    button.dataset.bigfanMobileProductBack = 'true';
    button.setAttribute('aria-label', 'Volver');
    button.innerHTML = '<span class="bigfan-mobile-product-back__icon" aria-hidden="true">←</span><span class="bigfan-mobile-product-back__label">Volver</span>';

    button.addEventListener('click', bigfanReturnFromProductPage);
    anchor.parentNode.insertBefore(button, anchor);
  };



  // Unified product card footer. Price and quick-add share the same real
  // flex row in every listing and featured collection, including cards added
  // by infinite scroll. Nothing is positioned over the other control.
  const initMobileCardImageCart = (root = document) => {
    // A section refresh or infinite scroll can insert raw cards at any time.
    // Reuse the existing row when present; do not create duplicates on re-init.
    const cards = Array.from(root.querySelectorAll?.('.product-card-wrapper > .card') || []);
    if (root.matches?.('.product-card-wrapper > .card')) cards.unshift(root);

    cards.forEach((card) => {
      const content = card.querySelector(':scope > .card__content');
      const information = content?.querySelector(':scope > .card__information');
      const price = information?.querySelector(':scope > .card-information > .price') ||
        content?.querySelector(':scope > .bigfan-price-cart-row > .price');
      if (!content || !information || !price) return;

      const quickAdd = content.querySelector(':scope > .quick-add') ||
        content.querySelector(':scope > .bigfan-price-cart-row > .quick-add');
      let row = content.querySelector(':scope > .bigfan-price-cart-row');
      if (!row) {
        row = document.createElement('div');
        row.className = 'bigfan-price-cart-row';
        content.appendChild(row);
      }
      if (price.parentElement !== row) row.appendChild(price);
      if (quickAdd?.querySelector('.quick-add__submit.bigfan-mobile-cart-icon-button, .quick-add__submit.bigfan-card-variant-cta')) {
        quickAdd.classList.remove('bigfan-quickadd-footer', 'bigfan-quickadd-overlay');
        if (quickAdd.parentElement !== row) row.appendChild(quickAdd);
      }
      card.classList.add('bigfan-card--price-cart');
    });
  };

  const initAll = (root = document) => {
    // Price/cart layout must not depend on completion of other initializers.
    initMobileCardImageCart(root);
    initFavorites(root);
    initFavoritesPage(root);
    storeCurrentProduct();
    renderRecentlyViewed(root);
    initStickyATC();
    initInfiniteScroll(root);
    initCollectionToTop();
    initProductCardGalleries(root);
    initProductMediaGalleryNavigation(root);
    // v1.7.52: restore pre-v1.6.69 normal full product-page navigation.
    initCatalogTabs(root);
    initCatalogHoverMenus(root);
    initHeaderSearchClear(root);
    initFilterDrawerHoverOpen(root);
    initFilterDrawerDismiss(root);
    initFilterAccordions(root);
    initMobileListingControls(root);
    initMobileProductBackButton(root);
    initMobileProductSwipeBack();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll()); else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('bigfan:stock-bootstrap-complete', () => initAll(document));

  const collectionObserver = new MutationObserver((mutations) => {
    const selector = '[data-bigfan-infinite]:not([data-bigfan-bound="true"]), [data-bigfan-favorite]:not([data-bigfan-bound="true"]), [data-bigfan-card-gallery]:not([data-bigfan-gallery-bound="true"]), [data-bigfan-product-gallery-nav]:not([data-bigfan-gallery-nav-bound="true"]), :is(#product-grid, .bigfan-featured-collection) .product-card-wrapper > .card:not(.bigfan-card--price-cart)';
    const changed = mutations.some((mutation) => [...mutation.addedNodes].some((node) => {
      if (node.nodeType !== 1) return false;
      return node.matches?.(selector) || Boolean(node.querySelector?.(selector));
    }));
    if (changed) initAll(document);
  });
  collectionObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
